import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Briefcase, Users, MessageSquare, Calendar, Star, ChevronRight,
  Filter, Search, MapPin, Building2, Clock, Eye, Share2, Send,
  Lock, Unlock, ExternalLink, X, Plus, Loader2, RefreshCw,
  ArrowRight, CheckCircle, AlertCircle, Globe, Mail, Phone, User, ArrowLeft
} from 'lucide-react';
import BackButton from '../components/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons and labels
const CATEGORY_CONFIG = {
  business_leads: { icon: Briefcase, label: 'Business Leads', color: 'text-blue-400 bg-blue-500/20' },
  partnerships: { icon: Users, label: 'Partnerships', color: 'text-purple-400 bg-purple-500/20' },
  consulting_requests: { icon: MessageSquare, label: 'Consulting', color: 'text-amber-400 bg-amber-500/20' },
  invitations: { icon: Mail, label: 'Invitations', color: 'text-green-400 bg-green-500/20' },
  events: { icon: Calendar, label: 'Events', color: 'text-pink-400 bg-pink-500/20' },
  others: { icon: Star, label: 'Others', color: 'text-zinc-400 bg-zinc-500/20' }
};

// Opportunity Card Component
const OpportunityCard = ({ opportunity, onView, onRespond, isSubscriber, user }) => {
  const config = CATEGORY_CONFIG[opportunity.category] || CATEGORY_CONFIG.others;
  const Icon = config.icon;
  
  return (
    <div 
      className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-all cursor-pointer group"
      onClick={() => onView(opportunity)}
      data-testid={`opportunity-card-${opportunity.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${config.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        {opportunity.is_teaser && (
          <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Preview
          </span>
        )}
      </div>
      
      {/* Title */}
      <h3 className="font-semibold text-lg mb-2 group-hover:text-[#C41E3A] transition-colors line-clamp-2">
        {opportunity.title}
      </h3>
      
      {/* Meta */}
      <div className="flex flex-wrap gap-2 mb-3 text-sm">
        {opportunity.sector && (
          <span className="text-zinc-400 bg-zinc-800 px-2 py-0.5 rounded">
            {opportunity.sector}
          </span>
        )}
        {opportunity.geography && (
          <span className="text-zinc-400 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {opportunity.geography}
          </span>
        )}
      </div>
      
      {/* Posted by */}
      {opportunity.linked_profile_name && (
        <div className="flex items-center gap-2 text-sm text-zinc-500 mb-3">
          <Building2 className="w-4 h-4" />
          <span>Posted by: </span>
          <Link 
            to={`/${opportunity.linked_profile_type === 'leadership' ? 'leader' : 'p'}/${opportunity.linked_profile_slug}`}
            className="text-[#C41E3A] hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {opportunity.linked_profile_name}
          </Link>
        </div>
      )}
      
      {/* Source badge */}
      {opportunity.source === 'admin' && (
        <span className="text-xs bg-[#1E3A5F]/30 text-[#6B9FD4] px-2 py-0.5 rounded">
          Via CONTINEX
        </span>
      )}
      
      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-800">
        <span className="text-xs text-zinc-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(opportunity.created_at).toLocaleDateString()}
        </span>
        <button 
          className="text-[#C41E3A] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
          data-testid={`view-opportunity-${opportunity.id}`}
        >
          View Details <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Opportunity Detail Modal
const OpportunityModal = ({ opportunity, onClose, user, isSubscriber, onRespond, onUnlock }) => {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [showUnlockForm, setShowUnlockForm] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState('');
  const [unlockMobile, setUnlockMobile] = useState('');
  const [responseData, setResponseData] = useState({
    responder_name: user?.name || '',
    responder_company: user?.company || '',
    responder_email: user?.email || '',
    responder_phone: '',
    responder_designation: '',
    message: '',
    marketing_consent: false
  });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const config = CATEGORY_CONFIG[opportunity.category] || CATEGORY_CONFIG.others;
  const Icon = config.icon;
  
  const handleUnlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onUnlock(opportunity.id, unlockEmail, unlockMobile, opportunity.referrer_id);
      setShowUnlockForm(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to unlock');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRespond = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onRespond(opportunity.id, responseData, opportunity.referrer_id);
      setShowResponseForm(false);
      alert('Your interest has been submitted! The opportunity poster will be notified.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit response');
    } finally {
      setLoading(false);
    }
  };
  
  const handleShare = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/connect/opportunities/${opportunity.id}/referral-link`, { withCredentials: true });
      await navigator.clipboard.writeText(res.data.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('Login required to share opportunities');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${config.color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <span className="text-sm text-zinc-400">{config.label}</span>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">{opportunity.title}</h2>
          
          {/* Teaser Lock Screen */}
          {opportunity.is_teaser && opportunity.requires_unlock && (
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6 text-center mb-6">
              <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Unlock Full Details</h3>
              <p className="text-zinc-400 mb-4">
                Enter your contact details to view the complete opportunity.
              </p>
              
              {!showUnlockForm ? (
                <button
                  onClick={() => setShowUnlockForm(true)}
                  className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2"
                >
                  <Unlock className="w-4 h-4" />
                  Unlock Now
                </button>
              ) : (
                <form onSubmit={handleUnlock} className="text-left space-y-4 max-w-sm mx-auto">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Email</label>
                    <input
                      type="email"
                      value={unlockEmail}
                      onChange={(e) => setUnlockEmail(e.target.value)}
                      required
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Mobile</label>
                    <input
                      type="tel"
                      value={unlockMobile}
                      onChange={(e) => setUnlockMobile(e.target.value)}
                      required
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'View Full Details'}
                  </button>
                  <p className="text-xs text-zinc-500 text-center">
                    Your contact info will only be shared if you express interest.
                  </p>
                </form>
              )}
            </div>
          )}
          
          {/* Full Content */}
          {!opportunity.is_teaser && (
            <>
              {/* Meta info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {opportunity.sector && (
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500 mb-1">Sector</p>
                    <p className="font-medium">{opportunity.sector}</p>
                  </div>
                )}
                {opportunity.geography && (
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500 mb-1">Geography</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-zinc-400" />
                      {opportunity.geography}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Description */}
              <div className="mb-6">
                <h4 className="text-sm text-zinc-400 mb-2 uppercase tracking-wide">Description</h4>
                <p className="text-zinc-300 whitespace-pre-wrap">{opportunity.description}</p>
              </div>
              
              {/* Action Needed */}
              {opportunity.action_needed && (
                <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <h4 className="text-sm text-amber-400 mb-2 uppercase tracking-wide flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Action Needed
                  </h4>
                  <p className="text-zinc-300">{opportunity.action_needed}</p>
                </div>
              )}
              
              {/* Contact Info */}
              {(opportunity.contact_name || opportunity.contact_email || opportunity.contact_phone) && (
                <div className="mb-6">
                  <h4 className="text-sm text-zinc-400 mb-2 uppercase tracking-wide">Contact</h4>
                  <div className="bg-zinc-800/50 rounded-lg p-4 space-y-2">
                    {opportunity.contact_name && (
                      <p className="flex items-center gap-2">
                        <User className="w-4 h-4 text-zinc-500" />
                        {opportunity.contact_name}
                      </p>
                    )}
                    {opportunity.contact_email && (
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-zinc-500" />
                        <a href={`mailto:${opportunity.contact_email}`} className="text-[#C41E3A] hover:underline">
                          {opportunity.contact_email}
                        </a>
                      </p>
                    )}
                    {opportunity.contact_phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-zinc-500" />
                        {opportunity.contact_phone}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Posted By Profile Link */}
              {opportunity.linked_profile_name && (
                <div className="mb-6 bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="text-sm text-zinc-400 mb-2 uppercase tracking-wide">Posted By</h4>
                  <Link
                    to={`/${opportunity.linked_profile_type === 'leadership' ? 'leader' : 'p'}/${opportunity.linked_profile_slug}`}
                    className="flex items-center gap-3 hover:bg-zinc-700/50 p-2 rounded-lg transition-colors"
                  >
                    <div className="w-10 h-10 bg-zinc-700 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                      <p className="font-semibold">{opportunity.linked_profile_name}</p>
                      <p className="text-sm text-[#C41E3A] flex items-center gap-1">
                        View Profile <ExternalLink className="w-3 h-3" />
                      </p>
                    </div>
                  </Link>
                </div>
              )}
              
              {/* Deadline */}
              {opportunity.deadline && (
                <div className="mb-6 text-sm text-zinc-400">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Deadline: {new Date(opportunity.deadline).toLocaleDateString()}
                </div>
              )}
            </>
          )}
          
          {/* Stats */}
          <div className="flex items-center gap-6 text-sm text-zinc-500 mb-6">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {opportunity.views || 0} views
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {new Date(opportunity.created_at).toLocaleDateString()}
            </span>
          </div>
          
          {/* Actions */}
          {!opportunity.is_teaser && (
            <div className="flex flex-wrap gap-3">
              {user ? (
                <>
                  {!showResponseForm ? (
                    <button
                      onClick={() => setShowResponseForm(true)}
                      className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center justify-center gap-2"
                      data-testid="express-interest-btn"
                    >
                      <Send className="w-4 h-4" />
                      Express Interest
                    </button>
                  ) : (
                    <form onSubmit={handleRespond} className="w-full space-y-4">
                      <h4 className="font-semibold text-lg">Express Your Interest</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Name *</label>
                          <input
                            type="text"
                            value={responseData.responder_name}
                            onChange={(e) => setResponseData({...responseData, responder_name: e.target.value})}
                            required
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Company *</label>
                          <input
                            type="text"
                            value={responseData.responder_company}
                            onChange={(e) => setResponseData({...responseData, responder_company: e.target.value})}
                            required
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Email *</label>
                          <input
                            type="email"
                            value={responseData.responder_email}
                            onChange={(e) => setResponseData({...responseData, responder_email: e.target.value})}
                            required
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-zinc-400 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={responseData.responder_phone}
                            onChange={(e) => setResponseData({...responseData, responder_phone: e.target.value})}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">Designation</label>
                        <input
                          type="text"
                          value={responseData.responder_designation}
                          onChange={(e) => setResponseData({...responseData, responder_designation: e.target.value})}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1">Message (optional)</label>
                        <textarea
                          value={responseData.message}
                          onChange={(e) => setResponseData({...responseData, message: e.target.value})}
                          rows={3}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2"
                          placeholder="Tell them why you're interested..."
                        />
                      </div>
                      
                      {/* Marketing Consent Checkbox - GDPR/DPDP Compliance */}
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id={`marketing_consent_${opportunity.id}`}
                          checked={responseData.marketing_consent}
                          onChange={(e) => setResponseData({...responseData, marketing_consent: e.target.checked})}
                          className="mt-1 w-4 h-4 bg-zinc-800 border-zinc-600 rounded text-[#C41E3A] focus:ring-[#C41E3A]"
                          data-testid="connect-marketing-consent"
                        />
                        <label htmlFor={`marketing_consent_${opportunity.id}`} className="text-zinc-400 text-xs leading-relaxed">
                          I agree to receive updates about similar opportunities and promotional content from BrandStage.
                        </label>
                      </div>
                      
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setShowResponseForm(false)}
                          className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-3 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        >
                          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Submit</>}
                        </button>
                      </div>
                    </form>
                  )}
                  
                  <button
                    onClick={handleShare}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-3 rounded-lg inline-flex items-center gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    {copied ? 'Copied!' : 'Share'}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold text-center"
                >
                  Login to Respond
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Connect Board Page
export default function ConnectBoard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    sector: '',
    geography: ''
  });
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [isSubscriber, setIsSubscriber] = useState(false);
  
  // Check if opening specific opportunity from URL
  const oppIdFromUrl = searchParams.get('opp');
  const refFromUrl = searchParams.get('ref');
  
  // Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch (err) {
        // Not logged in
      }
    };
    fetchUser();
  }, []);
  
  // Fetch opportunities
  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.geography) params.append('geography', filters.geography);
      params.append('page', pagination.page);
      params.append('limit', 20);
      
      const res = await axios.get(`${API_URL}/api/connect/opportunities?${params.toString()}`, { withCredentials: true });
      setOpportunities(res.data.opportunities);
      setPagination({
        page: res.data.page,
        total: res.data.total,
        totalPages: res.data.total_pages
      });
      setIsSubscriber(res.data.is_subscriber);
    } catch (err) {
      console.error('Failed to fetch opportunities:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);
  
  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);
  
  // Load specific opportunity from URL
  useEffect(() => {
    if (oppIdFromUrl) {
      const loadOpportunity = async () => {
        try {
          const url = refFromUrl 
            ? `${API_URL}/api/connect/opportunities/${oppIdFromUrl}?ref=${refFromUrl}`
            : `${API_URL}/api/connect/opportunities/${oppIdFromUrl}`;
          const res = await axios.get(url, { withCredentials: true });
          setSelectedOpportunity(res.data);
        } catch (err) {
          console.error('Failed to load opportunity:', err);
        }
      };
      loadOpportunity();
    }
  }, [oppIdFromUrl, refFromUrl]);
  
  // Handle unlock
  const handleUnlock = async (oppId, email, mobile, referrerId) => {
    await axios.post(`${API_URL}/api/connect/opportunities/${oppId}/unlock`, {
      email,
      mobile,
      opportunity_id: oppId,
      referrer_id: referrerId
    }, { withCredentials: true });
    
    // Reload the opportunity
    const res = await axios.get(`${API_URL}/api/connect/opportunities/${oppId}`, { withCredentials: true });
    setSelectedOpportunity(res.data);
  };
  
  // Handle respond
  const handleRespond = async (oppId, responseData, referrerId) => {
    await axios.post(`${API_URL}/api/connect/opportunities/${oppId}/respond`, {
      ...responseData,
      referred_by: referrerId
    }, { withCredentials: true });
  };
  
  // View opportunity
  const handleViewOpportunity = async (opp) => {
    try {
      const res = await axios.get(`${API_URL}/api/connect/opportunities/${opp.id}`, { withCredentials: true });
      setSelectedOpportunity(res.data);
    } catch (err) {
      console.error('Failed to load opportunity:', err);
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                BrandStage <span className="text-[#C41E3A]">Connect</span>
                <span className="ml-3 bg-amber-500/20 text-amber-400 text-sm px-3 py-1 rounded-full font-medium align-middle">Coming Soon</span>
              </h1>
              <p className="text-zinc-400 text-lg">
                Discover business opportunities, partnerships, and leads from verified members
              </p>
            </div>
          </div>
          
          {/* Back Button */}
          <div className="mt-6">
            <BackButton to="/" label="Back to Home" />
          </div>
          
          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mt-8">
            <button
              onClick={() => setFilters({...filters, category: ''})}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !filters.category 
                  ? 'bg-[#C41E3A] text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              All
            </button>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => setFilters({...filters, category: key})}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                    filters.category === key 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {config.label}
                </button>
              );
            })}
          </div>
          
          {/* Search / Filter Row */}
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by sector..."
                value={filters.sector}
                onChange={(e) => setFilters({...filters, sector: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-zinc-500 focus:border-[#C41E3A] focus:outline-none"
              />
            </div>
            <div className="flex-1 min-w-[200px] relative">
              <MapPin className="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Filter by geography..."
                value={filters.geography}
                onChange={(e) => setFilters({...filters, geography: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-zinc-500 focus:border-[#C41E3A] focus:outline-none"
              />
            </div>
            <button
              onClick={fetchOpportunities}
              className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2.5 rounded-lg inline-flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Apply
            </button>
          </div>
        </div>
      </div>
      
      {/* Subscription Banner */}
      {!isSubscriber && user && (
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D4A6F] border-b border-[#3D5A7F]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-400" />
              <p className="text-sm">
                <span className="font-semibold">Upgrade to Connect</span> to view full details, respond to opportunities, and post your own.
              </p>
            </div>
            <button className="bg-white text-[#1E3A5F] px-4 py-2 rounded-lg font-semibold text-sm hover:bg-zinc-100">
              Subscribe Now
            </button>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats - only show for logged in users */}
        {user && (
          <div className="flex items-center justify-between mb-6">
            <p className="text-zinc-400">
              {pagination.total} {pagination.total === 1 ? 'opportunity' : 'opportunities'} found
            </p>
            <button
              onClick={fetchOpportunities}
              className="text-zinc-400 hover:text-white inline-flex items-center gap-2 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        )}
        
        {/* Loading */}
        {loading && opportunities.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
          </div>
        ) : !user ? (
          /* Sign-in gate for non-authenticated users */
          <div className="text-center py-20 bg-zinc-900/50 border border-zinc-800 rounded-xl max-w-lg mx-auto">
            <div className="w-16 h-16 bg-[#C41E3A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-8 h-8 text-[#C41E3A]" />
            </div>
            <h3 className="text-2xl font-bold mb-3">Members-Only Access</h3>
            <p className="text-zinc-400 mb-6 px-6">
              BrandStage Connect opportunities are available exclusively to verified members. 
              Sign in to discover business leads, partnerships, and exclusive invitations.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center px-6">
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-500 text-white px-6 py-3 rounded-lg font-semibold"
              >
                Create Account
              </Link>
            </div>
            <p className="text-zinc-500 text-sm mt-6">
              Free to join. Create your profile to access opportunities.
            </p>
          </div>
        ) : opportunities.length === 0 ? (
          <div className="text-center py-20 bg-zinc-900/50 border border-zinc-800 rounded-xl">
            <Briefcase className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Opportunities Yet</h3>
            <p className="text-zinc-400 mb-6">
              Be the first to post an opportunity and connect with verified businesses.
            </p>
            <Link
              to="/connect/post"
              className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold"
            >
              <Plus className="w-5 h-5" />
              Post First Opportunity
            </Link>
          </div>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {opportunities.map(opp => (
                <OpportunityCard
                  key={opp.id}
                  opportunity={opp}
                  onView={handleViewOpportunity}
                  isSubscriber={isSubscriber}
                  user={user}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-10">
                <button
                  onClick={() => setPagination(p => ({...p, page: p.page - 1}))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 bg-zinc-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-zinc-400">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination(p => ({...p, page: p.page + 1}))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-4 py-2 bg-zinc-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Mobile Post Button */}
      {user && (
        <Link
          to="/connect/post"
          className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-[#C41E3A] hover:bg-[#A01830] rounded-full flex items-center justify-center shadow-lg z-40"
        >
          <Plus className="w-6 h-6" />
        </Link>
      )}
      
      {/* Detail Modal */}
      {selectedOpportunity && (
        <OpportunityModal
          opportunity={selectedOpportunity}
          onClose={() => setSelectedOpportunity(null)}
          user={user}
          isSubscriber={isSubscriber}
          onRespond={handleRespond}
          onUnlock={handleUnlock}
        />
      )}
    </div>
  );
}
